import React, { useState } from 'react';

import { DeleteOutline, EditOutlined, ExpandLess, ExpandMore } from '@mui/icons-material';
import BoltIcon from '@mui/icons-material/Bolt';
import CodeIcon from '@mui/icons-material/Code';
import {
	Box,
	Button,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	ListItemButton,
	ListItemText,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { CodeStoreFileModel, EventHandlerAndPayloadModel } from '../../../api/api';
import ContextCodeFileItem from '../../ContextDesigner/Components/ContextCodeFileItem';
import SchemaSection from '../../EntityDesigner/Components/SchemaSection';
import LogicDesignerPlaceholder from '../../OperationDesigner/Components/LogicDesignerPlaceholder';

interface EventHandlerItemProps {
	eventHandler: EventHandlerAndPayloadModel;
	eventHandlerCode: CodeStoreFileModel | null;
	onRefreshCodeFile?: (fileName: string) => void | Promise<void>;
	onSelect?: () => void;
	expanded?: boolean;
	onUpdate?: (handlerId: string, name: string, description: string, tags: string[]) => Promise<unknown>;
	onDelete?: (handlerId: string) => Promise<unknown>;
}

const EventHandlerItem: React.FC<EventHandlerItemProps> = ({ eventHandler, eventHandlerCode, onRefreshCodeFile, onSelect, expanded, onUpdate, onDelete }) => {
	const [open, setOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [name, setName] = useState(eventHandler.handler.name);
	const [description, setDescription] = useState(eventHandler.handler.description);
	const [tags, setTags] = useState(eventHandler.handler.tags.join(', '));
	const [saving, setSaving] = useState(false);
	const isOpen = expanded ?? open;

	const handleToggle = () => {
		const next = !isOpen;
		setOpen(next);
		if (next) onSelect?.();
	};

	const handleUpdate = async () => {
		if (!onUpdate || !name.trim()) return;
		setSaving(true);
		try {
			await onUpdate(
				eventHandler.handler.id,
				name.trim(),
				description.trim(),
				tags.split(',').map((tag) => tag.trim()).filter(Boolean),
			);
			setEditOpen(false);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!onDelete) return;
		setSaving(true);
		try {
			await onDelete(eventHandler.handler.id);
			setDeleteOpen(false);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box sx={{ mb: 3, border: '1px solid #ddd', borderRadius: 2 }}>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<BoltIcon color="warning" sx={{ mr: 2, ml: 2 }} />
				<ListItemButton onClick={handleToggle}>
					<ListItemText
						primary={
							<>
								<Typography component="span" variant="h6" sx={{ verticalAlign: 'middle', display: 'inline', mr: 1 }}>
									{eventHandler.handler.name}
								</Typography>
							</>
						}
						secondary={eventHandler.handler.description}
					/>
				</ListItemButton>
				{onUpdate && (
					<Tooltip title="Edit integration event handler">
						<IconButton size="small" onClick={() => setEditOpen(true)} aria-label="Edit integration event handler">
							<EditOutlined fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
				{onDelete && (
					<Tooltip title="Delete integration event handler">
						<IconButton size="small" color="error" onClick={() => setDeleteOpen(true)} aria-label="Delete integration event handler">
							<DeleteOutline fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
				<IconButton size="small" onClick={handleToggle} aria-label={isOpen ? 'Collapse integration event handler' : 'Expand integration event handler'} sx={{ mr: 1 }}>
					{isOpen ? <ExpandLess /> : <ExpandMore />}
				</IconButton>
			</Box>
			<Divider />
			{/* Expandable Section */}
			<Collapse in={isOpen} timeout="auto" unmountOnExit>
				<Box sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
						<CodeIcon color="info" sx={{ mr: 2, mt: 0.5 }} />
						<Box sx={{ flex: 1 }}>
							<SchemaSection schema={eventHandler.payload!} />
						</Box>
					</Box>

					<Box sx={{ my: 4 }}>
						<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }} gutterBottom>
							Logic Designer
						</Typography>
						{eventHandlerCode ? <ContextCodeFileItem file={eventHandlerCode} onRefresh={onRefreshCodeFile} /> : <LogicDesignerPlaceholder />}
					</Box>
				</Box>
				<Divider />
			</Collapse>
			<Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Edit Integration Event Handler</DialogTitle>
				<DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
					<TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} required autoFocus />
					<TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={3} />
					<TextField label="Tags" value={tags} onChange={(event) => setTags(event.target.value)} helperText="Separate tags with commas" />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
					<Button variant="contained" onClick={handleUpdate} disabled={saving || !name.trim()}>Save</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Delete Integration Event Handler?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This removes {eventHandler.handler.name} from this context. Generated code files are retained.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
					<Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>Delete</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default EventHandlerItem;
